import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  BarChart3, 
  Eye, 
  MessageSquare, 
  ShoppingCart, 
  Users, 
  TrendingUp,
  Activity,
  Car,
  Calendar,
  ExternalLink,
  Crown,
  Clock,
  Globe,
  MapPin,
  Filter,
  Search
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminAnalytics() {
  const [data, setData] = useState(null);
  const [filterStats, setFilterStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchAnalytics = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Fetch both overview and filter stats
      const [overviewRes, filterRes] = await Promise.all([
        fetch(`${API_URL}/api/analytics/overview`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/analytics/filter-stats`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);
      
      if (overviewRes.ok) {
        const result = await overviewRes.json();
        setData(result);
      } else {
        toast.error('Fehler beim Laden der Analytics');
      }
      
      if (filterRes.ok) {
        const filterResult = await filterRes.json();
        setFilterStats(filterResult);
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
      toast.error('Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
    // Refresh every 30 seconds
    const interval = setInterval(fetchAnalytics, 30000);
    return () => clearInterval(interval);
  }, []);

  const formatNumber = (num) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num?.toString() || '0';
  };

  const getImageUrl = (url) => {
    if (!url) return 'https://images.unsplash.com/photo-1503376780353-7e6692767b70?w=200';
    if (url.startsWith('http')) return url;
    return `${API_URL}${url}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading flex items-center gap-2" data-testid="analytics-title">
            <BarChart3 size={24} />
            Analytics
          </h1>
          <p className="text-sm text-[#666666] mt-1">
            Übersicht über Ihre Website-Performance
          </p>
        </div>
        <div className="flex items-center gap-2 text-sm text-[#666666]">
          <Activity size={16} className={data?.active_visitors > 0 ? 'text-green-500 animate-pulse' : 'text-gray-400'} />
          <span className="font-medium">{data?.active_visitors || 0}</span>
          <span>aktive Besucher</span>
        </div>
      </div>

      {/* Main Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white p-6 border border-[#E5E5E5] rounded-lg">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-blue-100 rounded-xl">
              <Eye size={24} className="text-blue-600" />
            </div>
            <div>
              <p className="text-3xl font-heading">{formatNumber(data?.total_page_views)}</p>
              <p className="text-sm text-[#666666]">Seitenaufrufe</p>
            </div>
          </div>
          <div className="mt-3 pt-3 border-t border-[#E5E5E5]">
            <span className="text-sm text-green-600 font-medium">+{data?.today_views || 0}</span>
            <span className="text-sm text-[#999999]"> heute</span>
          </div>
        </div>

        <div className="bg-white p-6 border border-[#E5E5E5] rounded-lg">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-purple-100 rounded-xl">
              <Car size={24} className="text-purple-600" />
            </div>
            <div>
              <p className="text-3xl font-heading">{formatNumber(data?.total_car_views)}</p>
              <p className="text-sm text-[#666666]">Fahrzeug-Aufrufe</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 border border-[#E5E5E5] rounded-lg">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-amber-100 rounded-xl">
              <MessageSquare size={24} className="text-amber-600" />
            </div>
            <div>
              <p className="text-3xl font-heading">{formatNumber(data?.total_inquiries)}</p>
              <p className="text-sm text-[#666666]">Anfragen</p>
            </div>
          </div>
          <div className="mt-3 pt-3 border-t border-[#E5E5E5]">
            <span className="text-sm text-green-600 font-medium">+{data?.today_inquiries || 0}</span>
            <span className="text-sm text-[#999999]"> heute</span>
          </div>
        </div>

        <div className="bg-white p-6 border border-[#E5E5E5] rounded-lg">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-emerald-100 rounded-xl">
              <ShoppingCart size={24} className="text-emerald-600" />
            </div>
            <div>
              <p className="text-3xl font-heading">{formatNumber(data?.total_cars_sold)}</p>
              <p className="text-sm text-[#666666]">Verkauft</p>
            </div>
          </div>
        </div>
      </div>

      {/* Secondary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-6 rounded-lg text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-4xl font-heading">{data?.total_customers || 0}</p>
              <p className="text-blue-100 text-sm mt-1">Kunden gesamt</p>
            </div>
            <Users size={32} className="text-blue-200" />
          </div>
        </div>
        <div className="bg-gradient-to-br from-purple-500 to-purple-600 p-6 rounded-lg text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-4xl font-heading">{data?.all_cars_stats?.length || 0}</p>
              <p className="text-purple-100 text-sm mt-1">Fahrzeuge im Bestand</p>
            </div>
            <Car size={32} className="text-purple-200" />
          </div>
        </div>
        <div className="bg-gradient-to-br from-green-500 to-green-600 p-6 rounded-lg text-white col-span-2 md:col-span-1">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-4xl font-heading flex items-center gap-2">
                {data?.active_visitors || 0}
                {data?.active_visitors > 0 && (
                  <span className="w-3 h-3 bg-white rounded-full animate-pulse" />
                )}
              </p>
              <p className="text-green-100 text-sm mt-1">Gerade online</p>
            </div>
            <Activity size={32} className="text-green-200" />
          </div>
        </div>
      </div>

      {/* Geo Stats - Countries & Cities */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Top Countries */}
        <div className="bg-white border border-[#E5E5E5] rounded-lg overflow-hidden">
          <div className="p-4 border-b border-[#E5E5E5] bg-gradient-to-r from-blue-50 to-indigo-50">
            <h3 className="font-heading text-lg flex items-center gap-2">
              <Globe size={20} className="text-blue-600" />
              Top 3 Länder
            </h3>
            <p className="text-xs text-[#666666] mt-1">Nach Seitenaufrufen</p>
          </div>
          
          {data?.top_countries?.length === 0 ? (
            <div className="p-8 text-center text-[#999999]">
              <Globe size={32} className="mx-auto mb-2 opacity-30" />
              <p className="text-sm">Noch keine Geo-Daten</p>
            </div>
          ) : (
            <div className="divide-y divide-[#E5E5E5]">
              {data?.top_countries?.map((item, index) => (
                <div key={item.country} className="p-4 flex items-center justify-between hover:bg-[#F9F9F7] transition-colors">
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                      index === 0 ? 'bg-amber-100 text-amber-700' :
                      index === 1 ? 'bg-gray-200 text-gray-700' :
                      'bg-orange-100 text-orange-700'
                    }`}>
                      {index + 1}
                    </div>
                    <span className="font-medium">{item.country}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Eye size={14} className="text-[#999999]" />
                    <span className="text-[#666666] font-medium">{formatNumber(item.views)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Top Cities */}
        <div className="bg-white border border-[#E5E5E5] rounded-lg overflow-hidden">
          <div className="p-4 border-b border-[#E5E5E5] bg-gradient-to-r from-emerald-50 to-teal-50">
            <h3 className="font-heading text-lg flex items-center gap-2">
              <MapPin size={20} className="text-emerald-600" />
              Top 10 Städte
            </h3>
            <p className="text-xs text-[#666666] mt-1">Nach Seitenaufrufen</p>
          </div>
          
          {data?.top_cities?.length === 0 ? (
            <div className="p-8 text-center text-[#999999]">
              <MapPin size={32} className="mx-auto mb-2 opacity-30" />
              <p className="text-sm">Noch keine Geo-Daten</p>
            </div>
          ) : (
            <div className="divide-y divide-[#E5E5E5] max-h-[320px] overflow-y-auto">
              {data?.top_cities?.map((item, index) => (
                <div key={item.city} className="p-3 flex items-center justify-between hover:bg-[#F9F9F7] transition-colors">
                  <div className="flex items-center gap-3">
                    <span className="w-6 h-6 rounded-full bg-[#F9F9F7] flex items-center justify-center text-xs text-[#666666] font-medium">
                      {index + 1}
                    </span>
                    <span className="text-sm">{item.city}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Eye size={12} className="text-[#999999]" />
                    <span className="text-sm text-[#666666]">{formatNumber(item.views)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Top 10 Cars */}
      <div className="bg-white border border-[#E5E5E5] rounded-lg overflow-hidden">
        <div className="p-6 border-b border-[#E5E5E5]">
          <h2 className="font-heading text-lg flex items-center gap-2">
            <Crown size={20} className="text-amber-500" />
            Top 10 Fahrzeuge
          </h2>
          <p className="text-sm text-[#666666] mt-1">Nach Anzahl der Aufrufe sortiert</p>
        </div>
        
        {data?.top_cars?.length === 0 ? (
          <div className="p-12 text-center text-[#999999]">
            <BarChart3 size={48} className="mx-auto mb-4 opacity-30" />
            <p>Noch keine Daten vorhanden</p>
          </div>
        ) : (
          <div className="divide-y divide-[#E5E5E5]">
            {data?.top_cars?.map((car, index) => (
              <div key={car.car_id} className="p-4 flex items-center gap-4 hover:bg-[#F9F9F7] transition-colors">
                {/* Rank */}
                <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                  index === 0 ? 'bg-amber-100 text-amber-700' :
                  index === 1 ? 'bg-gray-200 text-gray-700' :
                  index === 2 ? 'bg-orange-100 text-orange-700' :
                  'bg-[#F9F9F7] text-[#666666]'
                }`}>
                  {index + 1}
                </div>

                {/* Car Image */}
                <div className="relative">
                  <img
                    src={getImageUrl(car.image_url)}
                    alt={`${car.brand} ${car.model}`}
                    className={`w-20 h-14 object-cover rounded ${car.is_sold ? 'grayscale' : ''}`}
                  />
                  {car.is_sold && (
                    <span className="absolute top-1 left-1 px-1 text-[8px] font-bold bg-emerald-600 text-white rounded">
                      SOLD
                    </span>
                  )}
                </div>

                {/* Car Info */}
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{car.brand} {car.model}</p>
                  <p className="text-sm text-[#666666]">{car.year} • €{car.price?.toLocaleString()}</p>
                </div>

                {/* Stats */}
                <div className="flex items-center gap-6">
                  <div className="text-center">
                    <div className="flex items-center gap-1 text-blue-600">
                      <Eye size={14} />
                      <span className="font-bold">{car.views}</span>
                    </div>
                    <p className="text-xs text-[#999999]">Aufrufe</p>
                  </div>
                  <div className="text-center">
                    <div className="flex items-center gap-1 text-amber-600">
                      <MessageSquare size={14} />
                      <span className="font-bold">{car.inquiries}</span>
                    </div>
                    <p className="text-xs text-[#999999]">Anfragen</p>
                  </div>
                  <div className="text-center min-w-[60px]">
                    <div className={`font-bold ${car.inquiries > 0 && car.views > 0 ? 'text-green-600' : 'text-[#999999]'}`}>
                      {car.views > 0 ? ((car.inquiries / car.views) * 100).toFixed(1) : '0.0'}%
                    </div>
                    <p className="text-xs text-[#999999]">Konversion</p>
                  </div>
                </div>

                {/* Action */}
                <Link
                  to={`/admin/cars/${car.car_id}/edit`}
                  className="p-2 hover:bg-[#E5E5E5] rounded transition-colors"
                  title="Fahrzeug bearbeiten"
                >
                  <ExternalLink size={16} className="text-[#666666]" />
                </Link>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* All Cars Stats */}
      {data?.all_cars_stats?.length > 10 && (
        <div className="bg-white border border-[#E5E5E5] rounded-lg overflow-hidden">
          <div className="p-6 border-b border-[#E5E5E5]">
            <h2 className="font-heading text-lg">Alle Fahrzeuge</h2>
            <p className="text-sm text-[#666666] mt-1">Komplette Statistik-Übersicht</p>
          </div>
          
          <div className="overflow-x-auto">
            <table className="admin-table w-full">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Fahrzeug</th>
                  <th>Jahr</th>
                  <th>Preis</th>
                  <th>Aufrufe</th>
                  <th>Anfragen</th>
                  <th>Konversion</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {data?.all_cars_stats?.slice(10).map((car, index) => (
                  <tr key={car.car_id}>
                    <td className="text-[#999999]">{index + 11}</td>
                    <td>
                      <Link 
                        to={`/admin/cars/${car.car_id}/edit`}
                        className="font-medium hover:text-gold"
                      >
                        {car.brand} {car.model}
                      </Link>
                    </td>
                    <td>{car.year}</td>
                    <td>€{car.price?.toLocaleString()}</td>
                    <td>
                      <span className="flex items-center gap-1 text-blue-600">
                        <Eye size={12} />
                        {car.views}
                      </span>
                    </td>
                    <td>
                      <span className="flex items-center gap-1 text-amber-600">
                        <MessageSquare size={12} />
                        {car.inquiries}
                      </span>
                    </td>
                    <td>
                      {car.views > 0 ? ((car.inquiries / car.views) * 100).toFixed(1) : '0.0'}%
                    </td>
                    <td>
                      {car.is_sold ? (
                        <span className="px-2 py-1 bg-emerald-100 text-emerald-700 text-xs rounded-full">
                          Verkauft
                        </span>
                      ) : (
                        <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">
                          Verfügbar
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Popular Search Filters */}
      <div className="bg-white border border-[#E5E5E5] rounded-lg overflow-hidden">
        <div className="p-6 border-b border-[#E5E5E5]">
          <h2 className="font-heading text-lg flex items-center gap-2">
            <Filter size={20} className="text-indigo-500" />
            Beliebte Suchfilter
          </h2>
          <p className="text-sm text-[#666666] mt-1">
            Welche Filter werden am häufigsten verwendet?
            {filterStats?.total_filter_usage > 0 && (
              <span className="ml-2 text-indigo-600">({formatNumber(filterStats.total_filter_usage)} Filter-Nutzungen gesamt)</span>
            )}
          </p>
        </div>
        
        {!filterStats || filterStats.total_filter_usage === 0 ? (
          <div className="p-12 text-center text-[#999999]">
            <Search size={48} className="mx-auto mb-4 opacity-30" />
            <p>Noch keine Filter-Statistiken</p>
            <p className="text-xs mt-1">Daten werden gesammelt, sobald Besucher Filter verwenden</p>
          </div>
        ) : (
          <div className="p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
            {/* Top Brands */}
            <div>
              <h3 className="text-sm font-medium text-[#666666] mb-3 flex items-center gap-1">
                <Car size={14} />
                Top Marken
              </h3>
              {filterStats?.top_brands?.length > 0 ? (
                <ul className="space-y-2">
                  {filterStats.top_brands.slice(0, 5).map((item, idx) => (
                    <li key={item.value} className="flex items-center justify-between text-sm">
                      <span className="flex items-center gap-2">
                        <span className={`w-5 h-5 rounded text-xs flex items-center justify-center ${
                          idx === 0 ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'
                        }`}>{idx + 1}</span>
                        {item.value}
                      </span>
                      <span className="text-[#999999]">{item.count}×</span>
                    </li>
                  ))}
                </ul>
              ) : <p className="text-xs text-[#999999]">Keine Daten</p>}
            </div>
            
            {/* Top Fuel Types */}
            <div>
              <h3 className="text-sm font-medium text-[#666666] mb-3">Top Kraftstoffe</h3>
              {filterStats?.top_fuel_types?.length > 0 ? (
                <ul className="space-y-2">
                  {filterStats.top_fuel_types.slice(0, 5).map((item, idx) => (
                    <li key={item.value} className="flex items-center justify-between text-sm">
                      <span className="flex items-center gap-2">
                        <span className={`w-5 h-5 rounded text-xs flex items-center justify-center ${
                          idx === 0 ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                        }`}>{idx + 1}</span>
                        {item.value}
                      </span>
                      <span className="text-[#999999]">{item.count}×</span>
                    </li>
                  ))}
                </ul>
              ) : <p className="text-xs text-[#999999]">Keine Daten</p>}
            </div>
            
            {/* Top Transmissions */}
            <div>
              <h3 className="text-sm font-medium text-[#666666] mb-3">Top Getriebe</h3>
              {filterStats?.top_transmissions?.length > 0 ? (
                <ul className="space-y-2">
                  {filterStats.top_transmissions.slice(0, 5).map((item, idx) => (
                    <li key={item.value} className="flex items-center justify-between text-sm">
                      <span className="flex items-center gap-2">
                        <span className={`w-5 h-5 rounded text-xs flex items-center justify-center ${
                          idx === 0 ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'
                        }`}>{idx + 1}</span>
                        {item.value}
                      </span>
                      <span className="text-[#999999]">{item.count}×</span>
                    </li>
                  ))}
                </ul>
              ) : <p className="text-xs text-[#999999]">Keine Daten</p>}
            </div>
            
            {/* Top Colors */}
            <div>
              <h3 className="text-sm font-medium text-[#666666] mb-3">Top Farben</h3>
              {filterStats?.top_colors?.length > 0 ? (
                <ul className="space-y-2">
                  {filterStats.top_colors.slice(0, 5).map((item, idx) => (
                    <li key={item.value} className="flex items-center justify-between text-sm">
                      <span className="flex items-center gap-2">
                        <span className={`w-5 h-5 rounded text-xs flex items-center justify-center ${
                          idx === 0 ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-600'
                        }`}>{idx + 1}</span>
                        {item.value}
                      </span>
                      <span className="text-[#999999]">{item.count}×</span>
                    </li>
                  ))}
                </ul>
              ) : <p className="text-xs text-[#999999]">Keine Daten</p>}
            </div>
            
            {/* Top Vehicle Types */}
            <div>
              <h3 className="text-sm font-medium text-[#666666] mb-3">Top Fahrzeugtyp</h3>
              {filterStats?.top_vehicle_types?.length > 0 ? (
                <ul className="space-y-2">
                  {filterStats.top_vehicle_types.slice(0, 5).map((item, idx) => (
                    <li key={item.value} className="flex items-center justify-between text-sm">
                      <span className="flex items-center gap-2">
                        <span className={`w-5 h-5 rounded text-xs flex items-center justify-center ${
                          idx === 0 ? 'bg-orange-100 text-orange-700' : 'bg-gray-100 text-gray-600'
                        }`}>{idx + 1}</span>
                        {item.value}
                      </span>
                      <span className="text-[#999999]">{item.count}×</span>
                    </li>
                  ))}
                </ul>
              ) : <p className="text-xs text-[#999999]">Keine Daten</p>}
            </div>
          </div>
        )}
      </div>

      {/* Info Note */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Clock size={20} className="text-blue-600 mt-0.5" />
          <div>
            <p className="text-sm text-blue-800">
              <strong>Hinweis:</strong> Die Daten werden automatisch alle 30 Sekunden aktualisiert. 
              "Aktive Besucher" zeigt Nutzer, die in den letzten 5 Minuten aktiv waren.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
